
import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useFavorites } from "@/hooks/useFavorites";
import { Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { puttingTests, chippingTests, wedgingTests, bunkerTests, ironTests, fairwayWoodTests } from "@/data/level3/tests";
import { driverTests } from "@/data/level3/driverTests";
import { 
  puttingTests as level1PuttingTests, 
  chippingTests as level1ChippingTests, 
  wedgingTests as level1WedgingTests, 
  bunkerTests as level1BunkerTests, 
  ironTests as level1IronTests, 
  fairwayWoodTests as level1FairwayWoodTests, 
  driverTests as level1DriverTests 
} from "@/data/level1/tests";
import { 
  puttingTests as level2PuttingTests, 
  chippingTests as level2ChippingTests, 
  wedgingTests as level2WedgingTests, 
  bunkerTests as level2BunkerTests, 
  ironTests as level2IronTests, 
  fairwayWoodTests as level2FairwayWoodTests, 
  driverTests as level2DriverTests 
} from "@/data/level2/tests";
import FavoriteTestsHeader from "./favorites/FavoriteTestsHeader";
import FavoriteTestCard from "./favorites/FavoriteTestCard";
import { getTestRoute, getLevelName, getLevelBadgeVariant, getTestCategory, getTestIcon } from "./favorites/favoriteTestsUtils";

type TestType = "all" | "putting" | "chipping" | "wedging" | "bunker" | "iron" | "fairwayWood" | "driver";

const FavoriteTests: React.FC = () => {
  const { favorites, isLoading } = useFavorites();
  const [filterType, setFilterType] = useState<TestType>("all");
  const { t } = useTranslation('training');
  
  // Combine all tests for lookup including level1, level2 and level3 tests
  const allTests = [
    ...puttingTests, 
    ...chippingTests, 
    ...wedgingTests, 
    ...bunkerTests, 
    ...ironTests, 
    ...fairwayWoodTests, 
    ...driverTests,
    ...level1PuttingTests,
    ...level1ChippingTests,
    ...level1WedgingTests,
    ...level1BunkerTests,
    ...level1IronTests,
    ...level1FairwayWoodTests,
    ...level1DriverTests,
    ...level2PuttingTests,
    ...level2ChippingTests,
    ...level2WedgingTests,
    ...level2BunkerTests,
    ...level2IronTests,
    ...level2FairwayWoodTests,
    ...level2DriverTests
  ];
  
  // Get test details by test ID
  const getTestDetails = (testId: string) => {
    return allTests.find(test => test.id === testId);
  };

  // Filtrer les favoris en fonction du type sélectionné
  const filteredFavorites = filterType === "all" 
    ? favorites 
    : favorites.filter(fav => getTestCategory(fav.test_id) === filterType);
  
  if (isLoading) {
    return (
      <Card className="mb-6">
        <FavoriteTestsHeader filterType={filterType} setFilterType={setFilterType} />
        <CardContent>
          <p className="text-gray-500">{t('favorites.loading')}</p>
        </CardContent>
      </Card>
    );
  }
  
  if (favorites.length === 0) {
    return (
      <Card className="mb-6">
        <FavoriteTestsHeader filterType={filterType} setFilterType={setFilterType} />
        <CardContent>
          <p className="text-gray-500">{t('favorites.noFavorites')}</p>
          <p className="text-gray-500 text-sm mt-2">
            {t('favorites.noFavoritesHelp')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6">
      <FavoriteTestsHeader filterType={filterType} setFilterType={setFilterType} />
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {filteredFavorites.map((favorite) => {
            const testDetails = getTestDetails(favorite.test_id);
            if (!testDetails) return null;
            
            return (
              <FavoriteTestCard
                key={favorite.id}
                favorite={favorite}
                testDetails={testDetails}
                getTestRoute={getTestRoute}
                getTestIcon={getTestIcon}
                getLevelName={getLevelName}
                getLevelBadgeVariant={getLevelBadgeVariant}
              />
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default FavoriteTests;
